import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Users, TrendingUp } from 'lucide-react';

interface LocationData {
  country: string;
  state: string;
  count: number;
  averageScore: number;
}

interface LocationHeatmapProps {
  data: LocationData[];
}

const LocationHeatmap: React.FC<LocationHeatmapProps> = ({ data }) => {
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

  // Group data by country
  const countryData = data.reduce((acc, location) => {
    if (!acc[location.country]) {
      acc[location.country] = {
        totalUsers: 0,
        averageScore: 0,
        states: []
      };
    }
    
    acc[location.country].totalUsers += location.count;
    acc[location.country].states.push(location);
    
    return acc;
  }, {} as Record<string, { totalUsers: number; averageScore: number; states: LocationData[] }>);

  // Calculate average scores for countries
  Object.keys(countryData).forEach(country => {
    const states = countryData[country].states;
    const totalScore = states.reduce((sum, state) => sum + (state.averageScore * state.count), 0);
    const totalUsers = states.reduce((sum, state) => sum + state.count, 0);
    countryData[country].averageScore = totalUsers > 0 ? Math.round(totalScore / totalUsers) : 0;
  });

  const maxUsers = Math.max(...Object.values(countryData).map(c => c.totalUsers));

  const getIntensityColor = (userCount: number) => {
    const intensity = userCount / maxUsers;
    if (intensity > 0.8) return 'bg-primary-600';
    if (intensity > 0.6) return 'bg-primary-500';
    if (intensity > 0.4) return 'bg-primary-400';
    if (intensity > 0.2) return 'bg-primary-300';
    return 'bg-primary-200';
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-success-600';
    if (score >= 60) return 'text-warning-600';
    return 'text-error-600';
  };

  return (
    <div className="space-y-6">
      {/* Country Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(countryData)
          .sort(([,a], [,b]) => b.totalUsers - a.totalUsers)
          .map(([country, info], index) => (
            <motion.div
              key={country}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selectedCountry === country 
                  ? 'border-primary-500 bg-primary-50' 
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setSelectedCountry(selectedCountry === country ? null : country)}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-800">{country}</h3>
                <div className={`w-4 h-4 rounded-full ${getIntensityColor(info.totalUsers)}`} />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">{info.totalUsers} users</span>
                </div>
                
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4 text-gray-500" />
                  <span className={`font-medium ${getScoreColor(info.averageScore)}`}>
                    {info.averageScore}% avg score
                  </span>
                </div>
                
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">{info.states.length} states</span>
                </div>
              </div>
            </motion.div>
          ))}
      </div>

      {/* State Details */}
      {selectedCountry && (
        <motion.div
          className="bg-gray-50 rounded-xl p-6"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <h3 className="text-lg font-bold text-gray-800 mb-4">
            {selectedCountry} - State Breakdown
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {countryData[selectedCountry].states
              .sort((a, b) => b.count - a.count)
              .map((state, index) => (
                <motion.div
                  key={`${state.country}-${state.state}`}
                  className="bg-white p-4 rounded-lg shadow-sm"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-800">{state.state}</h4>
                    <div className={`w-3 h-3 rounded-full ${getIntensityColor(state.count)}`} />
                  </div>
                  
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Users:</span>
                      <span className="font-medium">{state.count}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">Avg Score:</span>
                      <span className={`font-medium ${getScoreColor(state.averageScore)}`}>
                        {state.averageScore}%
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
          </div>
        </motion.div>
      )}

      {/* Legend */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <h4 className="font-semibold text-gray-800 mb-3">Legend</h4>
        <div className="flex flex-wrap gap-6">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">User Density</div>
            <div className="flex items-center gap-2">
              {[
                { color: 'bg-primary-200', label: 'Low' },
                { color: 'bg-primary-300', label: '' },
                { color: 'bg-primary-400', label: '' },
                { color: 'bg-primary-500', label: '' },
                { color: 'bg-primary-600', label: 'High' }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-1">
                  <div className={`w-3 h-3 rounded-full ${item.color}`} />
                  {item.label && <span className="text-xs text-gray-600">{item.label}</span>}
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Score Range</div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-success-500 rounded-full" />
                <span className="text-xs text-gray-600">80%+ (Ready)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-warning-500 rounded-full" />
                <span className="text-xs text-gray-600">60-79% (Almost Ready)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-error-500 rounded-full" />
                <span className="text-xs text-gray-600">&lt;60% (Not Ready)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationHeatmap;